import {
    forwardRef,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type InputHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../utils';

interface DateInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    hideLabel?: boolean;
}

interface CalendarPosition {
    left: number;
    width: number;
    maxHeight: number;
    top?: number;
    bottom?: number;
}

const MONTH_FORMATTER = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' });
const DATE_FORMATTER = new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
});
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function parseIsoDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
}

function toIsoDate(date: Date): string {
    const year = String(date.getFullYear()).padStart(4, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function sameDay(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear()
        && left.getMonth() === right.getMonth()
        && left.getDate() === right.getDate();
}

function buildCalendarDays(month: Date): Array<Date | null> {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const firstDayOffset = (new Date(year, monthIndex, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const cells: Array<Date | null> = Array.from({ length: firstDayOffset }, () => null);

    for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push(new Date(year, monthIndex, day));
    }

    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

function calculateCalendarPosition(trigger: DOMRect): CalendarPosition {
    const margin = 12;
    const gap = 8;
    const desiredWidth = 336;
    const desiredHeight = 390;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(desiredWidth, viewportWidth - margin * 2);
    const centeredLeft = trigger.left + trigger.width / 2 - width / 2;
    const left = Math.min(Math.max(centeredLeft, margin), viewportWidth - margin - width);
    const below = viewportHeight - trigger.bottom - gap - margin;
    const above = trigger.top - gap - margin;
    const openAbove = below < desiredHeight && above > below;
    const availableHeight = Math.max(260, openAbove ? above : below);

    return {
        left,
        width,
        maxHeight: Math.min(desiredHeight, availableHeight, viewportHeight - margin * 2),
        ...(openAbove
            ? { bottom: viewportHeight - trigger.top + gap }
            : { top: Math.max(margin, trigger.bottom + gap) }),
    };
}

/** Calendario reutilizable que permanece dentro del viewport del app. */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
    ({
        label,
        value,
        onChange,
        error,
        className,
        id,
        disabled = false,
        hideLabel = false,
        min,
        max,
        placeholder = 'Selecciona una fecha',
        name,
        required,
        ...props
    }, forwardedRef) => {
        const generatedId = useId();
        const inputId = id || generatedId;
        const labelId = `${inputId}-label`;
        const calendarId = `${inputId}-calendar`;
        const errorId = `${inputId}-error`;
        const triggerRef = useRef<HTMLButtonElement>(null);
        const calendarRef = useRef<HTMLDivElement>(null);
        const selectedDate = parseIsoDate(value);
        const today = useMemo(() => {
            const current = new Date();
            return new Date(current.getFullYear(), current.getMonth(), current.getDate());
        }, []);
        const [isOpen, setIsOpen] = useState(false);
        const [visibleMonth, setVisibleMonth] = useState(
            () => new Date((selectedDate ?? today).getFullYear(), (selectedDate ?? today).getMonth(), 1),
        );
        const [position, setPosition] = useState<CalendarPosition | null>(null);
        const calendarDays = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);
        const minDate = min ? parseIsoDate(String(min)) : null;
        const maxDate = max ? parseIsoDate(String(max)) : null;

        const updatePosition = () => {
            const trigger = triggerRef.current?.getBoundingClientRect();
            if (trigger) setPosition(calculateCalendarPosition(trigger));
        };

        useEffect(() => {
            if (!isOpen) return;

            const handlePointerDown = (event: PointerEvent) => {
                const target = event.target as Node;
                if (!triggerRef.current?.contains(target) && !calendarRef.current?.contains(target)) {
                    setIsOpen(false);
                }
            };
            const handleKeyDown = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    setIsOpen(false);
                    triggerRef.current?.focus();
                }
            };
            const handleViewportChange = () => updatePosition();

            document.addEventListener('pointerdown', handlePointerDown);
            document.addEventListener('keydown', handleKeyDown);
            window.addEventListener('resize', handleViewportChange);
            window.addEventListener('scroll', handleViewportChange, true);

            requestAnimationFrame(() => {
                const selected = calendarRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
                const todayButton = calendarRef.current?.querySelector<HTMLButtonElement>('[data-today="true"]');
                (selected ?? todayButton ?? calendarRef.current?.querySelector<HTMLButtonElement>('[data-day]'))
                    ?.focus({ preventScroll: true });
            });

            return () => {
                document.removeEventListener('pointerdown', handlePointerDown);
                document.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('resize', handleViewportChange);
                window.removeEventListener('scroll', handleViewportChange, true);
            };
        }, [isOpen, visibleMonth]);

        const selectDate = (date: Date) => {
            onChange(toIsoDate(date));
            setIsOpen(false);
            triggerRef.current?.focus();
        };

        const changeMonth = (amount: number) => {
            setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
        };

        const toggleCalendar = () => {
            if (isOpen) {
                setIsOpen(false);
                return;
            }

            const referenceDate = selectedDate ?? today;
            setVisibleMonth(new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1));
            updatePosition();
            setIsOpen(true);
        };

        const isOutsideRange = (date: Date) => (
            Boolean(minDate && date < minDate) || Boolean(maxDate && date > maxDate)
        );

        const calendarStyle: CSSProperties | undefined = position
            ? {
                left: position.left,
                width: position.width,
                maxHeight: position.maxHeight,
                top: position.top,
                bottom: position.bottom,
            }
            : undefined;

        return (
            <div className={cn('app-form-field flex flex-col gap-2', className)}>
                <span
                    id={labelId}
                    className={cn('text-sm font-medium text-text-secondary', hideLabel && 'sr-only')}
                >
                    {label}
                </span>
                <input
                    ref={forwardedRef}
                    id={`${inputId}-value`}
                    type="hidden"
                    name={name}
                    value={value}
                    disabled={disabled}
                    aria-hidden="true"
                    {...props}
                />
                <button
                    ref={triggerRef}
                    id={inputId}
                    type="button"
                    className={cn(
                        'flex min-h-12 w-full touch-manipulation items-center rounded-xl border bg-surface px-4 py-3 text-left text-base transition-colors',
                        'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
                        disabled && 'cursor-not-allowed bg-disabled-bg opacity-60',
                        error ? 'border-error focus-visible:ring-error/25' : 'border-border',
                    )}
                    onClick={toggleCalendar}
                    disabled={disabled}
                    aria-labelledby={labelId}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-controls={calendarId}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    aria-required={required}
                >
                    <span className={cn('min-w-0 flex-1 truncate', selectedDate ? 'text-text' : 'text-text-muted')}>
                        {selectedDate ? DATE_FORMATTER.format(selectedDate) : placeholder}
                    </span>
                    <Calendar className="ml-2 size-5 shrink-0 text-text-muted" aria-hidden="true" />
                </button>

                {error ? (
                    <span id={errorId} className="text-xs text-error" role="alert">
                        {error}
                    </span>
                ) : null}

                {isOpen && position && typeof document !== 'undefined'
                    ? createPortal(
                        <div
                            ref={calendarRef}
                            id={calendarId}
                            role="dialog"
                            aria-modal="false"
                            aria-labelledby={`${calendarId}-title`}
                            className="fixed z-[1000] overflow-y-auto overscroll-contain rounded-2xl border border-border bg-surface p-4 shadow-pop"
                            style={calendarStyle}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <button
                                    type="button"
                                    className="flex size-12 touch-manipulation items-center justify-center rounded-xl text-text-secondary hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    onClick={() => changeMonth(-1)}
                                    aria-label="Mes anterior"
                                >
                                    <ChevronLeft size={22} aria-hidden="true" />
                                </button>
                                <h2 id={`${calendarId}-title`} className="text-center text-base font-semibold capitalize text-text">
                                    {MONTH_FORMATTER.format(visibleMonth)}
                                </h2>
                                <button
                                    type="button"
                                    className="flex size-12 touch-manipulation items-center justify-center rounded-xl text-text-secondary hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    onClick={() => changeMonth(1)}
                                    aria-label="Mes siguiente"
                                >
                                    <ChevronRight size={22} aria-hidden="true" />
                                </button>
                            </div>

                            <div className="mt-2 grid grid-cols-7 gap-1" aria-hidden="true">
                                {WEEKDAYS.map((weekday, index) => (
                                    <span key={`${weekday}-${index}`} className="flex h-8 items-center justify-center text-xs font-semibold text-text-muted">
                                        {weekday}
                                    </span>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-1">
                                {calendarDays.map((date, index) => date ? (
                                    <button
                                        key={toIsoDate(date)}
                                        type="button"
                                        data-day="true"
                                        data-today={sameDay(date, today) ? 'true' : undefined}
                                        aria-label={DATE_FORMATTER.format(date)}
                                        aria-pressed={Boolean(selectedDate && sameDay(date, selectedDate))}
                                        disabled={isOutsideRange(date)}
                                        onClick={() => selectDate(date)}
                                        className={cn(
                                            'flex aspect-square min-h-10 touch-manipulation items-center justify-center rounded-full text-sm font-semibold tabular-nums transition-colors',
                                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                                            selectedDate && sameDay(date, selectedDate)
                                                ? 'bg-primary text-white'
                                                : 'text-text hover:bg-hover active:bg-primary/15',
                                            sameDay(date, today) && !(selectedDate && sameDay(date, selectedDate)) && 'ring-1 ring-inset ring-primary text-primary',
                                            isOutsideRange(date) && 'cursor-not-allowed opacity-30',
                                        )}
                                    >
                                        {date.getDate()}
                                    </button>
                                ) : <span key={`empty-${index}`} aria-hidden="true" />)}
                            </div>

                            <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                                <button
                                    type="button"
                                    className="min-h-11 touch-manipulation rounded-lg px-3 text-sm font-semibold text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-40"
                                    onClick={() => {
                                        onChange('');
                                        setIsOpen(false);
                                        triggerRef.current?.focus();
                                    }}
                                    disabled={!value || required}
                                >
                                    Borrar
                                </button>
                                <button
                                    type="button"
                                    className="min-h-11 touch-manipulation rounded-lg px-3 text-sm font-semibold text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    onClick={() => {
                                        setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                                        if (!isOutsideRange(today)) selectDate(today);
                                    }}
                                >
                                    Hoy
                                </button>
                            </div>
                        </div>,
                        document.body,
                    )
                    : null}
            </div>
        );
    },
);

DateInput.displayName = 'DateInput';
